import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import cloneDeep from 'lodash.clonedeep';

import { NgUtils, StringUtils, TiimePipesModule } from 'tiime-expert-utils';
import { TiimeInputContainerModule, TiimeSelectModule, TiimeOptionModule } from 'tiime-material';

import { SelectorBase } from '@bases/selector/selector.base';
import { ErrorMessage } from '@interfaces/error-message';
import { TimeTrackingTaskByCategory } from '@interfaces/time-tracking-task-by-category';
import { TimeTrackingsCategory } from '@models/time-trackings-category';
import { TimeTrackingsTask } from '@models/time-trackings-task';
import { ListErrorComponent } from '@shared-components/list-error/list-error.component';
import { FormFocusObserveDirective } from '@shared-directives/form-focus-observe/form-focus-observe.directive';
import { TimeTrackingTaskLabelPipe } from '@shared-pipes/time-tracking-task-label/time-tracking-task-label.pipe';

@Component({
  selector: 'app-time-trackings-task-select',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TiimeInputContainerModule,
    TiimeSelectModule,
    TiimeOptionModule,
    ListErrorComponent,
    FormFocusObserveDirective,
    TiimePipesModule,
    TimeTrackingTaskLabelPipe
  ],
  templateUrl: './time-trackings-task-select.component.html',
  styleUrls: ['./time-trackings-task-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimeTrackingsTaskSelectComponent extends SelectorBase<{
  category: TimeTrackingsCategory;
  tasks: TimeTrackingsTask[];
}> {
  @Input() control: FormControl<number | null>;
  @Input('tasks') set setTasks(tasks: TimeTrackingsTask[]) {
    if (tasks) {
      const taskByCategories = tasks.reduce(
        (acc: TimeTrackingTaskByCategory[], timeTrackingsTask: TimeTrackingsTask) => {
          const index = acc.findIndex(ttc => ttc.category.id === timeTrackingsTask.timeTrackingCategory?.id);
          if (index !== -1) {
            acc[index].tasks.push(timeTrackingsTask);
          } else {
            acc.push({ category: timeTrackingsTask.timeTrackingCategory, tasks: [timeTrackingsTask] });
          }
          return acc;
        },
        []
      );

      this.selectorData = taskByCategories;
      this.filteredSelectorData = taskByCategories;
    }
  }
  @Input() small = false;
  @Input() required = false;
  @Input() errorMessage: ErrorMessage = null;
  @Input() label = 'Tâche';
  @Input() errorMessageMode: 'label' | 'list' = 'label';
  @Input() allowEmpty = true;

  @Output() readonly selectionChange: EventEmitter<TimeTrackingsTask | null> =
    new EventEmitter<TimeTrackingsTask | null>();

  readonly trackById = NgUtils.trackById;
  readonly trackByIndex = NgUtils.trackByIndex;

  emitSelectionChange(task: TimeTrackingsTask | null): void {
    this.selectionChange.emit(task);
  }

  filterFunction(search: string): TimeTrackingTaskByCategory[] {
    const clonedData: TimeTrackingTaskByCategory[] = cloneDeep(this.selectorData);
    return clonedData.filter((timeTrackingTaskByCategory: TimeTrackingTaskByCategory) => {
      timeTrackingTaskByCategory.tasks = timeTrackingTaskByCategory.tasks.filter(
        (task: TimeTrackingsTask) =>
          StringUtils.normalizeToLowerCase(task.code ?? '').includes(StringUtils.normalizeToLowerCase(search)) ||
          StringUtils.normalizeToLowerCase(task.wording).includes(StringUtils.normalizeToLowerCase(search))
      );
      return timeTrackingTaskByCategory.tasks?.length > 0;
    });
  }
}
